import { z } from 'zod';
/**
 * Контракты страницы «Моё» — `GET /api/my/items`.
 *
 * Кросс-пространственный список записей текущего пользователя: записи, где
 * он исполнитель (значение любого поля типа `user` содержит его id) и/или
 * автор (`items.created_by_id`). Скоуп прав зеркалит глобальный поиск:
 * только пространства, где пользователь участник (platform-admin — все).
 *
 * Статус резолвится в человекочитаемую метку и категорию (`config.options`
 * статусного поля — select с `hasCategories`), а не сырой код опции
 * (lens-pitfalls: raw id vs label). Дедлайн — best-effort: берётся значение
 * date-поля, чьё имя/slug похожи на срок («дедлайн», «срок», due, deadline,
 * «окончание», «завершение»); если такого поля у типа нет — null, чтобы не
 * выдавать дату старта за срок.
 */
// ─── GET /api/my/items (query) ───────────────────────────────────────────
//
// `role` — фильтр «в какой роли я причастен»; по умолчанию all.
// `limit` — целое 1..200, по умолчанию 100; невалидное значение клампится
// в хендлере (контракт «не падать», как у /api/search).
export const MyItemsRoleFilterSchema = z.enum(['all', 'assignee', 'author']);
export const MyItemsQuerySchema = z.object({
    role: MyItemsRoleFilterSchema.optional(),
    limit: z.number().int().min(1).max(200).optional(),
});
// ─── Статус записи (резолвленный) ────────────────────────────────────────
//
// `category` берётся из `option.category` статусного поля; null — у опции
// категория не задана либо опция не найдена в конфиге (тогда label = value).
export const MyItemStatusSchema = z.object({
    value: z.string(),
    label: z.string(),
    category: z.enum(['backlog', 'active', 'done', 'cancelled']).nullable(),
});
// ─── Запись в списке «Моё» ───────────────────────────────────────────────
//
// `roles` непустой: хотя бы одна из причин попадания в список.
// Навигация на фронте: `/s/${spaceSlug}?item=${displayKey ?? itemId}`.
export const MyItemRoleSchema = z.enum(['assignee', 'author']);
export const MyItemSchema = z.object({
    itemId: z.string().uuid(),
    spaceId: z.string().uuid(),
    spaceName: z.string(),
    spaceSlug: z.string(),
    spaceIcon: z.string().nullable(),
    displayKey: z.string().nullable(),
    title: z.string().nullable(),
    itemTypeId: z.string().uuid(),
    itemTypeName: z.string(),
    roles: z.array(MyItemRoleSchema).min(1),
    status: MyItemStatusSchema.nullable(),
    /** ISO-дата `YYYY-MM-DD` дедлайн-подобного date-поля; null — поля нет. */
    dueDate: z.string().nullable(),
    /** Имя поля, из которого взят dueDate (для подписи в UI). */
    dueFieldName: z.string().nullable(),
    updatedAt: z.string(),
});
// ─── Ответ GET /api/my/items ─────────────────────────────────────────────
//
// `total` — полное число подходящих записей (до limit), чтобы UI мог
// показать «показаны N из M».
export const MyItemsResponseSchema = z.object({
    items: z.array(MyItemSchema),
    total: z.number().int(),
    limit: z.number().int(),
});
